import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    Button,
    Chip
} from '@mui/material';
import {
    Event as CalendarIcon,
    FlightTakeoff as FlightIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { timeOffService } from '../../../services/timeOffService';
import { TimeOffRequest } from '../../../types';

interface ExtendedTimeOffRequest extends TimeOffRequest {
    dates?: string[]; // Synthetic property for aggregated dates
    endDate?: string; // Synthetic property
}

const NextTimeOffWidget: React.FC = () => {
    const { user } = useAuth();
    const [balances, setBalances] = useState<{ paidLeave: number; rr: number } | null>(null);
    const [nextRequest, setNextRequest] = useState<ExtendedTimeOffRequest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (!user) return;

            try {
                // Fetch user's time off requests and balances
                const [requests, balanceData] = await Promise.all([
                    timeOffService.getTimeOffRequestsByUser(user.id),
                    timeOffService.getUserHolidayBalances()
                ]);

                // Calculate total balances
                let paidLeave = 0;
                let rr = 0;

                balanceData.forEach(b => {
                    if (b.category === 'paid_leave') paidLeave += b.balance;
                    if (b.category === 'rr') rr += b.balance;
                });

                setBalances({ paidLeave, rr });

                // Filter for future approved requests
                const now = new Date();
                now.setHours(0, 0, 0, 0);

                const approvedFutureRequests = requests
                    .filter(req => req.status === 'approved')
                    .filter(req => {
                        const date = new Date(req.date);
                        return date >= now;
                    })
                    .sort((a, b) => {
                        const dateA = new Date(a.date).getTime();
                        const dateB = new Date(b.date).getTime();
                        return dateA - dateB;
                    });

                if (approvedFutureRequests.length > 0) {
                    // Find the first block of consecutive days
                    const firstRequest = approvedFutureRequests[0];
                    const block = [firstRequest];

                    for (let i = 1; i < approvedFutureRequests.length; i++) {
                        const current = approvedFutureRequests[i];
                        const prev = approvedFutureRequests[i - 1];

                        const currentDate = new Date(current.date);
                        const prevDate = new Date(prev.date);

                        // Check if consecutive (allowing for weekends if we want to get fancy, but simple +1 day diff for now)
                        const diffTime = Math.abs(currentDate.getTime() - prevDate.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays <= 3) { // Allow small gaps (weekends) to group into one "trip"
                            block.push(current);
                        } else {
                            break;
                        }
                    }

                    // Construct a synthetic "request" object that represents the block
                    const combinedRequest = {
                        ...firstRequest,
                        dates: block.map(r => r.date), // synthetic property
                        endDate: block[block.length - 1].date
                    };

                    setNextRequest(combinedRequest as any);
                } else {
                    setNextRequest(null);
                }
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch time off data', err);
                setError('Failed to load time off data');
                setLoading(false);
            }
        };

        fetchData();
    }, [user]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;
    }

    if (error) {
        return <Typography color="error" variant="body2">{error}</Typography>;
    }

    const renderBalances = () => (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-around' }}>
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">Paid Leave</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">{balances?.paidLeave.toFixed(1) || '0.0'}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">R&R</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">{balances?.rr.toFixed(1) || '0.0'}</Typography>
            </Box>
        </Box>
    );

    if (!nextRequest) {
        return (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        No upcoming time off scheduled.
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/time-off')}
                        startIcon={<FlightIcon />}
                    >
                        Request Time Off
                    </Button>
                </Box>
                {renderBalances()}
            </Box>
        );
    }

    // Calculate days until
    // @ts-ignore - we added dates property manually
    const startDateStr = nextRequest.dates ? nextRequest.dates[0] : nextRequest.date;
    const startDate = new Date(startDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = startDate.getTime() - today.getTime();
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) - 1;

    // @ts-ignore
    const countDays = nextRequest.dates ? nextRequest.dates.length : 1;

    return (
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        p: 1.5,
                        borderRadius: '50%',
                        mr: 2,
                        display: 'flex'
                    }}>
                        <FlightIcon />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                            {daysUntil <= 0 ? 'Today!' : `In ${daysUntil} ${daysUntil === 1 ? 'day' : 'days'}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {startDate.toLocaleDateString()}
                            {countDays > 1 && ` - ${new Date(nextRequest.endDate || startDateStr).toLocaleDateString()}`}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {nextRequest.holidayType}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={`${countDays} ${countDays === 1 ? 'Day' : 'Days'}`}
                            size="small"
                            icon={<TimeIcon />}
                            variant="outlined"
                        />
                        {nextRequest.halfDayPeriod && (
                            <Chip label={`${nextRequest.halfDayPeriod}`} size="small" color="info" variant="outlined" />
                        )}
                    </Box>
                </Box>

                {nextRequest.notes && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, fontStyle: 'italic' }}>
                        "{nextRequest.notes}"
                    </Typography>
                )}

                <Button
                    fullWidth
                    variant="text"
                    size="small"
                    onClick={() => navigate('/time-off')}
                >
                    View Details
                </Button>
            </Box>

            {renderBalances()}
        </Box>
    );
};

export default NextTimeOffWidget;
